import React, { useMemo, useState, useSyncExternalStore } from 'react';
import { DataTableProps } from 'primereact/datatable';
import { Column, ColumnEvent } from 'primereact/column';
import { Row } from 'primereact/row';
import { ColumnGroup } from 'primereact/columngroup';
import useIsReadOnlyMode from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import usePropertyPath from '../hooks/usePropertyPath';
import useProperty from '../hooks/useProperty';
import DynamicGridHeader from 'components/v2/compiler/grids/DynamicGridHeader';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { cellNumberEditor, cellTextEditor } from 'components/v2/compiler/grids/GridCellEditors';
import { cellTotalTemplate, numericCellBody, textCellBody } from 'components/v2/compiler/grids/GridService';
import useNestedErrors from '../hooks/useNestedErrors';
import { useDocumentStore } from '../hooks/useDocumentStore';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';

type ColumnDefinition = {
  propertyId: string;
  headerText?: string;
  headerHtml?: string;
  editor: 'text' | 'number';
  /** Use this property for different heading text.
   * For hiding/showing column "visibilityRule" must be used instead (as it's also accounted in the validation) */
  condition?: {
    propertyId: string;
    equals: any;
  };
};

type NoteDefintion = {
  labelText?: string;
  labelHtml?: string;
  noteText?: string;
  noteHtml?: string;
};

interface Props {
  propertyId: string;
  caption: string;
  columns: ColumnDefinition[];
  showTotalRow?: boolean;
  notes?: NoteDefintion[];
}

const AddableRowsGridRenderer: React.FC<Props> = ({ propertyId, caption, columns, showTotalRow, notes }) => {
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, isVisible, isDisabled } = useProperty<any[]>(propertyPath);
  const errorMessages = useNestedErrors(propertyPath);
  const visibleColumns = useColumnsVisiblity(propertyPath, columns);

  const {
    rows,
    dataTableProps,
    checkboxColumnNode,
    handleCellEdit,
    handleAddRow,
    handleDeleteSelected,
    isDeleteDisabled,
    gridId,
  } = useDynamicFormsGridRowSelectionViaCheckbox(value, setValue, isDisabled);

  if (!isVisible) {
    return null;
  }

  const rowClassName = () => ({ 'p-disabled': isDisabled });

  const header = (
    <DynamicGridHeader
      caption={caption}
      error={errorMessages.length > 0 && 'Required'}
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
      disabled={isDisabled}
    />
  );

  const footerGroup = (
    <ColumnGroup>
      {showTotalRow && (
        <Row>
          <Column className="p-row-total" />
          <Column className="p-row-total" footer="Total" />
          {visibleColumns.slice(1).map((col, i) => (
            <Column key={i} className="p-row-total" footer={cellTotalTemplate(col.propertyId, rows)} />
          ))}
        </Row>
      )}
      {notes?.map((note, i) => (
        <Row key={i}>
          <Column className="p-row-note" />
          <Column
            className="p-row-note"
            footer={
              note.labelHtml ? <div dangerouslySetInnerHTML={{ __html: note.labelHtml }} /> : <div>{note.labelText}</div>
            }
          />
          <Column
            className="p-row-note"
            footer={note.noteHtml ? <div dangerouslySetInnerHTML={{ __html: note.noteHtml }} /> : <div>{note.noteText}</div>}
            colSpan={visibleColumns.length - 1}
          />
        </Row>
      ))}
    </ColumnGroup>
  );

  return (
    <DataTableStyled
      key={gridId}
      header={header}
      footerColumnGroup={footerGroup}
      tableStyle={{ minWidth: '49rem' }}
      showGridlines
      editMode="cell"
      rowClassName={rowClassName}
      {...(dataTableProps as any)}
    >
      {checkboxColumnNode}
      {visibleColumns.map((col, i) => {
        let editor = undefined;
        let cellBody = undefined;
        if (!isDisabled) {
          switch (col.editor) {
            case 'number':
              editor = (options: any) =>
                cellNumberEditor({ ...options, value: options.value ?? null }, { maxFractionDigits: 8 });
              cellBody = numericCellBody({ placeholder: 'Type here' });
              break;
            case 'text':
              editor = (options: any) => cellTextEditor({ ...options, value: options.value ?? '' });
              cellBody = textCellBody({ placeholder: 'Type here' });
              break;
          }
        }
        return (
          <Column
            key={i}
            field={col.propertyId}
            header={col.headerText ?? <span dangerouslySetInnerHTML={{ __html: col.headerHtml ?? '' }} />}
            editor={editor}
            onCellEditComplete={handleCellEdit}
            body={cellBody}
          />
        );
      })}
    </DataTableStyled>
  );
};

export default AddableRowsGridRenderer;

function useDynamicFormsGridRowSelectionViaCheckbox(value: any[], setValue: (value: any[]) => void, isReadOnly: boolean) {
  const rows = value.length === 0 ? [{}] : value;
  const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);

  // Hack, to force re-render of the table, when selected rows are deleted, this is needed for edit controls to "forget" previously entered values,
  const [gridId, setGridId] = useState<string>(new Date().getTime().toString());

  const handleCellEdit = (ev: ColumnEvent) => {
    setValue(rows.map((row, index) => (index === ev.rowIndex ? { ...row, [ev.field]: ev.newValue } : row)));
  };

  const handleAddRow = () => {
    setValue([...rows, {}]);
  };

  const handleDeleteSelected = () => {
    setValue(rows.filter((_, index) => !selectedRowIndices.includes(index)));
    setSelectedRowIndices([]);
    setGridId(new Date().getTime().toString());
  };

  const isDeleteDisabled = isReadOnly || !selectedRowIndices?.length;

  return {
    rows,
    dataTableProps: {
      value: rows,
      ...(!isReadOnly && {
        selectionMode: 'checkbox',
        selection: rows.filter((_: any, index: number) => selectedRowIndices.includes(index)),
        onSelectionChange: (e) => setSelectedRowIndices(e.value.map((el: any) => rows.indexOf(el))),
        compareSelectionBy: 'equals',
      }),
    } satisfies Partial<DataTableProps<any>>,
    checkboxColumnNode: <Column selectionMode={isReadOnly ? undefined : 'multiple'} headerStyle={{ width: '3rem' }} />,
    handleCellEdit,
    handleAddRow,
    handleDeleteSelected,
    isDeleteDisabled,
    gridId,
  };
}

function useColumnsVisiblity(basePath: JsonPointerPath, columns: ColumnDefinition[]): ColumnDefinition[] {
  const store = useDocumentStore();

  const callbacks = useMemo(() => {
    const getActualState = () => {
      // This might not be the most efficient way, as this will rerun on any property change.
      // To whoever reads this in the future: consider implementing "refs"/"refId" subcriptions to the store,
      // so that we can rerun functions only when relevant properties change.
      return columns.map(
        (col) =>
          (!col.condition || store.getValue([col.condition.propertyId]) === col.condition.equals) &&
          store.getIsVisible([...basePath, 0, col.propertyId])
      );
    };

    let previousState = getActualState();

    const isSameState = (a: ReturnType<typeof getActualState>, b: ReturnType<typeof getActualState>) =>
      a.length === b.length && a.every((value, index) => value === b[index]);

    return {
      subscribe: (callback: () => void) => {
        const memoizedCallback = () => {
          const newState = getActualState();
          if (!isSameState(previousState, newState)) {
            previousState = newState;
            callback();
          }
        };

        return store.addDataChangedListener(memoizedCallback);
      },
      getState: () => previousState,
    };
  }, columns);

  const visibilityArray = useSyncExternalStore(callbacks.subscribe, callbacks.getState);

  const filteredColumns = useMemo(() => {
    return columns.filter((_, i) => visibilityArray[i]);
  }, [columns, visibilityArray]);

  return filteredColumns;
}
